// The Briefing for an open Research Pack (frame 40007883:371495).
//
//   📄  Briefing Prada sunglasses - Research pack        📖 Story
//   🖼  2 Photos
//   💬  2 snippets
//   ✓   1 task
//
// Same shape as every other briefing, and the same idea as the story one: the counts ARE the
// filters, so clicking "2 Photos" narrows the middle to the photos. What the pack adds is a second
// title action — the Summary ⇄ Story switch, which the capsule above the box used to carry as a
// pill pair.

import { briefing, countLabel } from './rows'
import { kindItems } from './kindItems'
import { isEmailResearchItem } from '@/lib/researchEmailItem'

// busy-my-web#609 (Saffet 2026-10-09): the shared `countLabel` — thousands separator, capital after the count.
const plural = (n, word) => countLabel(n, word)

// busy-my-web#146 — Saffet, 2026-08-21: "Can we remove the 1 task and and put close in front of the
// task where it writes open as it is open." An ACTIVE kind row lists its own items directly
// underneath it (BriefingBlock.vue's kind-item list), so the count above them says again exactly
// what the list below is showing — "1 task" over a list of one task.
//
// The COUNT goes and the row stays: its × is the only way back out of the filter (with a filter on,
// every other row hides), so deleting the row would trap the Briefing inside it. A row that is not
// active keeps its count — that is the whole reason to click it.
const bareCount = (text) => String(text).replace(/^([\d,]+)\s+(.+)$/, (_, n, word) => (n === '1' ? `${word}s` : word))


/**
 * What a pack holds, counted the way the capsule's pills counted it.
 *
 * A snippet is anything saved that is not a photo, a link, or one of the pack's own artefacts
 * (comparison / conclusion / the write-up doc) — the rule researchBriefing.js and the timeline's
 * pack node (storySteps.js's `packNode`) both already count by. This used to name the two kinds it
 * knew, note and receipt, which was the same set until the search rebuild (a77ae48) started saving
 * videos and products: the timeline's catch-all kept counting those as snippets while this list
 * did not, and the Briefing said "1 snippet" over a pack whose timeline node said 3
 * (busy-my-web#231, Saffet: "In the timeline we have 4 photos 3 snippets in the Briefing we have
 * we have 4 photos but just 1 snippet"). One rule, not two: whatever the timeline calls a snippet,
 * the Briefing counts.
 *
 * A saved EMAIL is not one of them (busy-timeline-srv#35). `fileResearchItem` takes it out before
 * the snippet fallback — it has a thread behind it, so it is something to OPEN, not a body of text
 * — and counting it here as a snippet broke the invariant above in the other direction: the
 * Briefing said "2 snippets" over a pack the timeline said held 2 emails, and clicking the row
 * opened the Notes tab, which holds no emails at all. Asked with `isEmailResearchItem`, the same
 * question the timeline asks, so an `email` kind with no thread id keeps falling through to
 * snippets on BOTH sides rather than vanishing from the strip. That is also why it is asked with a
 * function instead of joining the list below: what makes an item an email is the thread id, not
 * the word.
 *
 * A saved VIDEO is not one either (busy-my-web#546, Pouria: "when we select videos it should
 * calculate as videos not extractions"). It has its own card (a thumbnail/red play glyph, never
 * the generic document icon a snippet gets — Workboard.vue draws it that way already), so counting
 * it here read as "2 Extractions" over a pack that held a clip and nothing to read.
 */
const NOT_A_SNIPPET = ['photo', 'video', 'link', 'comparison', 'conclusion', 'doc']

export function countPack(items, events) {
  const list = (items || []).filter(Boolean)
  const evts = (events || []).filter(Boolean)
  const of = (kind) => list.filter(i => i.kind === kind).length

  return {
    photos: of('photo'),
    videos: of('video'),
    snippets: list.filter(i => !NOT_A_SNIPPET.includes(i.kind) && !isEmailResearchItem(i)).length,
    links: of('link'),
    emails: list.filter(isEmailResearchItem).length,
    comparisons: of('comparison'),
    tasks: evts.filter(e => e.kind === 'task').length,
    events: evts.filter(e => e.kind === 'event').length,
    // A NOTE added inside a pack is a real Note entity, so it arrives as a story event tagged with
    // the pack — not as a saved research item, which is why it never showed up among the snippets
    // above (busy-my-web#110, Saffet: "I just added a note to the research pack but the Briefing is
    // not updated"). Counted the way the story briefing counts its own notes.
    notes: evts.filter(e => e.kind === 'note').length,
  }
}

/**
 * The kinds that exist ONLY inside a pack — the rows above that the story's own timeline has no
 * filter for (busy-my-web#441).
 *
 * `task`, `event`, `note` and `email` are shared with the story briefing: narrowing a pack to its
 * tasks and narrowing the story to its tasks are the same `storyFilters.kind`, and one is a
 * perfectly good place to be left standing in when the pack closes. The four here are not — the
 * story timeline has no "photos" to narrow to, so a pack-scoped kind left behind narrows the middle
 * to nothing at all. That is the difference StoryView's pack-close cleanup is really about.
 */
const PACK_ONLY_KINDS = ['photo', 'snippet', 'video', 'link', 'comparison']

/** Is this kind one a pack alone can be narrowed to? See `PACK_ONLY_KINDS`. */
export function isPackOnlyKind(kind) {
  return PACK_ONLY_KINDS.includes(String(kind || ''))
}

/**
 * @param pack the focused Research Pack
 * @param counts what it holds — see countPack
 * @param view the middle's current view: 'doc' (Summary) or 'story'
 * @param filterKind the story kind narrowing the pack, if any — 'task' / 'event' / 'note'
 * @param opts `{ story, children }` — the ongoing this pack belongs to, so the chosen kind's items
 *        can be LISTED under their row (frame 40007892:377596). The pack itself does not carry its
 *        tasks; they are the story's timeline events tagged with this pack.
 */
/**
 * The pack rows that are VIEWS of the middle rather than filters over the strip (Saffet, 2026-10-21:
 * "when I open the research pack the Briefing doesn't have the details in it").
 *
 * Photos, Extractions, videos, links and emails each switch the pack page's middle to a tab — they
 * narrow nothing in the Briefing itself and list nothing under their row. They used to be treated
 * like the story kinds (task / event / note), whose active row takes the strip over: the moment the
 * Extractions tab was on, every other count stepped aside, the crumb took the row's place, and the
 * open pack's Briefing was its name line and nothing else. A view row now leaves the rest of the
 * card standing and adds no crumb of its own — the pack's "<name>, Open" chip stays where you are.
 */
export const PACK_VIEW_ROWS = new Set(['photo', 'snippet', 'video', 'link', 'email'])

/**
 * Does this active row take the strip over (the other rows step aside, the crumb carries it)?
 * Everything does, except a pack's view rows.
 * @param {string} briefingKind  the briefing the row belongs to ('pack', 'story', 'emails', …)
 * @param {object} row           a briefing row (`{ filter, active, … }`)
 */
export function rowTakesStrip(briefingKind, row) {
  return !(briefingKind === 'pack' && PACK_VIEW_ROWS.has(row?.filter))
}

export function packBriefing(pack, counts = {}, view = 'doc', filterKind = null, opts = {}) {
  if (!pack) return null

  // Which row is "on": the three content views mark themselves against what the middle is
  // showing, since clicking one SWITCHES the middle into it. The story-kind rows mark themselves
  // purely off the filter — they no longer force the middle to the Story view first (Pouria,
  // 2026-08-18: "when we click to 2 tasks we need to keep doc open"), so an active "2 tasks" row
  // must light up whether the middle is still showing the doc or has moved on its own.
  // `email` shares the LINKS view with `link` because that is where the pack draws its email cards
  // (StoryView.vue's links tab, above the links): a saved email is the same kind of thing as a
  // link, a reference with one place to go. Two rows over one view, so both light up together —
  // which is honest, since the tab really is showing both.
  const VIEW = { photo: 'images', snippet: 'notes', video: 'links', link: 'links', email: 'links' }
  const isOn = (key) => (VIEW[key] ? view === VIEW[key] : filterKind === key)

  const rows = []
  // The count drops off an ACTIVE row, whose items are listed right under it (busy-my-web#146).
  // …and `full` keeps that count for the breadcrumb, which names the same depth as a place
  // (busy-my-web#188) — "2 tasks" — where the row above the list of them does not.
  // …except a VIEW row (PACK_VIEW_ROWS), which lists nothing under itself and keeps its count.
  const row = (key, icon, text, count) => { if (count) rows.push({ key, icon, text: isOn(key) && !PACK_VIEW_ROWS.has(key) ? bareCount(text) : text, full: text, filter: key, active: isOn(key) }) }

  // The design's own order: photos, then Extractions (the frames still say "snippets"; Pouria renamed the
  // kind to "Extractions" everywhere on 2026-09-15), then the things you can act on.
  row('photo', 'gallery', plural(counts.photos, 'Photo'), counts.photos)
  row('snippet', 'message-text', plural(counts.snippets, 'Extraction'), counts.snippets)
  // Videos and links as themselves (Pouria, 2026-10-01: "we need to include videos and links as
  // well in briefing"). A video had no row at all — counted since #546, drawn nowhere — and the
  // links row said "product". Both open the pack's Links view, where the videos are listed above
  // the links.
  row('video', 'gallery', plural(counts.videos, 'video'), counts.videos)
  row('link', 'global', plural(counts.links, 'link'), counts.links)
  // Saved emails, next to the links they sit beside in the middle (busy-timeline-srv#35). Their own
  // row rather than a share of the snippets: clicking "2 snippets" opened the Notes tab, which
  // never holds an email, so a pack whose only content was two saved threads had no row that could
  // reach them at all. The glyph is the email module's own (`sms`, lib/moduleIcons.js) — the same
  // one the card in the middle wears, so the row and what it opens read as one thing.
  row('email', 'sms', plural(counts.emails, 'email'), counts.emails)
  row('task', 'tick-circle', plural(counts.tasks, 'task'), counts.tasks)
  row('event', 'events', plural(counts.events, 'event'), counts.events)
  // Notes take the story briefing's own wording and glyph, and sit where it puts them — after the
  // things you can act on. Like the task/event rows it is a story-kind row: clicking it lists the
  // pack's notes underneath (kindItems already answers for 'note') instead of switching the middle.
  row('note', 'notes', plural(counts.notes, 'note'), counts.notes)
  // Last, after the things you can act on — frame 40008221:412348 writes this strip
  // "2 Photos · 2 snippets · 2 tasks · 1 comparison" (#77). Its glyph is the two-bubble
  // `messages-3` the story step uses (Pouria, 2026-08-18), so the same event reads the same in
  // both places.
  row('comparison', 'messages-3', plural(counts.comparisons, 'comparison'), counts.comparisons)

  // "2 tasks" clicked INSIDE a pack lists those tasks under the row, each openable — the same
  // treatment the story briefing gives its kinds, narrowed to this pack (step 5). Only the story
  // kinds have items to list: the photo/snippet/link rows are VIEWS of the pack's contents, and the
  // middle is already showing them.
  // No longer gated on `view === 'story'`: that gate assumed clicking a kind row always switched
  // the middle there first, which it no longer does (Pouria, 2026-08-18) — the list has to show
  // whatever the middle is currently doing, doc included, or there would be nothing to choose from.
  const items = filterKind
    ? kindItems(opts.story, opts.children, filterKind, pack.id)
    : null

  return briefing({
    kind: 'pack',
    title: `Briefing ${pack.title || 'Research pack'} - Research pack`,
    titleIcon: 'receipt-text',
    // The Briefing ⇄ Story switch, beside the title rather than in the rows: it changes HOW the
    // pack is shown, not WHICH of its contents you are looking at. It names the view it will take
    // you TO, so the label is the destination rather than where you already are — the old pill
    // pair showed both at once, and there is no room for a pair here.
    titleAction: {
      key: 'view',
      icon: view === 'story' ? 'receipt-text' : 'story',
      text: view === 'story' ? 'Briefing' : 'Story',
      active: view === 'story',
    },
    rows,
    kindItems: items,
  })
}
